import React from 'react';
import Header from '../components/landing/Header';
import Footer from '../components/landing/Footer';
import { siteConfig } from '../data/mock';

const TermsOfService = () => {
  return (
    <div className="min-h-screen">
      <Header />
      <main className="pt-16">
        <div className="bg-bg-primary py-20">
          <div className="px-6 lg:px-12 xl:px-20 max-w-4xl mx-auto">
            <h1 className="text-4xl md:text-5xl font-medium text-text-primary mb-8">
              Terms of Service
            </h1>
            <p className="text-sm text-text-light mb-8">
              Last updated: {new Date().toLocaleDateString('en-GB', { year: 'numeric', month: 'long', day: 'numeric' })}
            </p>

            <div className="prose prose-lg max-w-none text-text-secondary space-y-8">
              <section>
                <h2 className="text-2xl font-medium text-text-primary mt-8 mb-4">1. Introduction</h2>
                <p>
                  Welcome to HighReach Apps. These Terms of Service ("Terms") govern your use of our website 
                  building and client portal services ("Services") provided by HighReach Apps ("we", "us", or "our").
                </p>
                <p>
                  By accessing or using our Services, you agree to be bound by these Terms. If you disagree with 
                  any part of these Terms, you may not access or use our Services.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-medium text-text-primary mt-8 mb-4">2. Our Services</h2>
                <p>
                  HighReach Apps provides website building and client portal services specifically designed for 
                  hair and beauty businesses. Our Services include:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Custom-designed websites tailored to your business</li>
                  <li>Online booking systems</li>
                  <li>Client portals for managing appointments</li>
                  <li>Mobile-friendly experiences for your clients</li>
                  <li>SEO-ready pages</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-medium text-text-primary mt-8 mb-4">3. Free Preview Period</h2>
                <p>
                  We build your website and client portal completely free of charge. You will receive a preview 
                  link to review the website and portal we have built specifically for your business.
                </p>
                <p>
                  This preview is provided at no cost and with no obligation. If you decide not to proceed, 
                  the preview website and portal will be removed after 14 days.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-medium text-text-primary mt-8 mb-4">4. Hosting and Payment</h2>
                <p>
                  If you decide to go live with your website and client portal, hosting fees apply as follows:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Base hosting:</strong> £30 per month (includes one website and one user account)</li>
                  <li><strong>Additional users:</strong> £10 per month each (typically for staff access or shared management)</li>
                  <li><strong>Unlimited clients:</strong> Unlimited clients can book and use the platform at no additional cost</li>
                </ul>
                <p>
                  You only pay once the site is live and being used. There are no setup fees, no build fees, 
                  and no contracts. You may cancel your hosting subscription at any time.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-medium text-text-primary mt-8 mb-4">5. Customisation and Changes</h2>
                <p>
                  The preview website and portal we provide is a bespoke starting point built specifically for 
                  your business. Before going live, we can tailor it further to match your preferences, including:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Colours and branding</li>
                  <li>Layout and structure</li>
                  <li>Wording and content</li>
                  <li>Images and media</li>
                </ul>
                <p>
                  Nothing is locked in at the preview stage. We will work with you to make any reasonable 
                  adjustments before going live.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-medium text-text-primary mt-8 mb-4">6. Optional Upgrades</h2>
                <p>
                  Your standard setup includes automated booking confirmations, reminders, and online payments. 
                  Optional extras may include:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Loyalty or membership programmes</li>
                  <li>Marketing tools</li>
                  <li>Advanced reporting</li>
                </ul>
                <p>
                  These optional upgrades are only added if you request them and will be discussed and agreed 
                  upon separately.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-medium text-text-primary mt-8 mb-4">7. Your Responsibilities</h2>
                <p>You agree to:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Provide accurate information about your business</li>
                  <li>Use the Services in compliance with all applicable laws and regulations</li>
                  <li>Not use the Services for any illegal or unauthorised purpose</li>
                  <li>Maintain the security of your account credentials</li>
                  <li>Notify us immediately of any unauthorised use of your account</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-medium text-text-primary mt-8 mb-4">8. Intellectual Property</h2>
                <p>
                  Once your website and client portal go live, you own the website and its content. Your SEO 
                  benefits your business, and your website is your asset.
                </p>
                <p>
                  However, the underlying platform, software, and technology remain the property of HighReach Apps. 
                  You may not copy, modify, or distribute our platform or software.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-medium text-text-primary mt-8 mb-4">9. Limitation of Liability</h2>
                <p>
                  HighReach Apps provides Services "as is" and "as available". We do not guarantee that the 
                  Services will be uninterrupted, error-free, or completely secure.
                </p>
                <p>
                  To the maximum extent permitted by law, HighReach Apps shall not be liable for any indirect, 
                  incidental, special, consequential, or punitive damages, or any loss of profits or revenues, 
                  whether incurred directly or indirectly.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-medium text-text-primary mt-8 mb-4">10. Termination</h2>
                <p>
                  You may cancel your hosting subscription at any time. Upon cancellation, your website and 
                  client portal will be taken offline, and you will no longer be charged hosting fees.
                </p>
                <p>
                  We reserve the right to suspend or terminate your access to the Services if you breach these 
                  Terms or engage in any fraudulent, abusive, or illegal activity.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-medium text-text-primary mt-8 mb-4">11. Changes to Terms</h2>
                <p>
                  We reserve the right to modify these Terms at any time. We will notify you of any material 
                  changes by posting the new Terms on this page and updating the "Last updated" date.
                </p>
                <p>
                  Your continued use of the Services after any changes constitutes acceptance of the new Terms.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-medium text-text-primary mt-8 mb-4">12. Contact Information</h2>
                <p>
                  If you have any questions about these Terms of Service, please contact us:
                </p>
                <ul className="list-none space-y-2">
                  <li>Email: {siteConfig.email}</li>
                  <li>Phone: {siteConfig.phone}</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-medium text-text-primary mt-8 mb-4">13. Governing Law</h2>
                <p>
                  These Terms shall be governed by and construed in accordance with the laws of the United Kingdom, 
                  without regard to its conflict of law provisions.
                </p>
              </section>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default TermsOfService;
